import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { Form, Input, InputNumber, message, TimePicker } from "antd";
import dayjs from "dayjs";
import CustomModal from "components/Common/CustomModal";
import CommonSelectLite from "components/Common/CommonSelectLite";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { parseSQLDate } from "utils/dateUtils";
import { ClassData } from "types/classTypes";
import { TeacherJournal } from "types/teacherJournalTypes";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

interface AddEditTeacherJournalModalProps {
  isOpen: boolean;
  onClose: () => void;
  journalData: TeacherJournal | null;
  onSave: (data: any) => Promise<void> | void;
  classes: ClassData[];
  forTeacher: Boolean;
}

// helper pour convertir "08:00 - 09:00" -> [dayjs, dayjs]
const parsePeriodLabelToRange = (label?: string | null) => {
  if (!label) return null;
  const match = label.match(/(\d{1,2}:\d{2}).*?(\d{1,2}:\d{2})/);
  if (!match) return null;

  const start = dayjs(match[1], "HH:mm");
  const end = dayjs(match[2], "HH:mm");
  if (!start.isValid() || !end.isValid()) return null;

  return [start, end] as [dayjs.Dayjs, dayjs.Dayjs];
};

const AddEditTeacherJournalModal: React.FC<AddEditTeacherJournalModalProps> = ({
  isOpen,
  onClose,
  journalData,
  onSave,
  classes,
  forTeacher
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);

  const selectedClass = useMemo(
    () => classes.find((c) => c.id === selectedClassId) || null,
    [classes, selectedClassId]
  );

  const classOptions = useMemo(
    () =>
      (classes || []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [classes]
  );

  const subjectOptions = useMemo(() => {
    if (!selectedClass) return [];
    return selectedClass.classSubject?.map((cs: any) => ({
      value: cs.subjectId,
      label: getSubjectLabelFromCode(cs.subject?.code) || cs.subjectId,
    }));
  }, [selectedClass]);

  useEffect(() => {
    if (journalData) {
      setSelectedClassId(journalData.classId || null);
      const timeRange = parsePeriodLabelToRange(journalData.periodLabel);

      form.setFieldsValue({
        classId: journalData.classId,
        subjectId: journalData.subjectId,
        date: journalData.date ? parseSQLDate(journalData.date) : null,
        sessionNumber: journalData.sessionNumber || null,
        timeRange: timeRange || null,
        competency: journalData.competency || "",
        objectives: journalData.objectives || "",
        prerequisites: journalData.prerequisites || "",
        materials: journalData.materials || "",
        methodology: journalData.methodology || "",
        evaluation: journalData.evaluation || "",
        homework: journalData.homework || "",
        notes: journalData.notes || "",
      });

      const cls = classes.find((c) => c.id === journalData.classId);
      const cs = cls?.classSubject?.find(
        (item: any) => item.subjectId === journalData.subjectId
      );

      if (cs?.teacherId) {
        form.setFieldsValue({ teacherId: cs.teacherId });
      } else if (journalData.teacherId) {
        form.setFieldsValue({ teacherId: journalData.teacherId });
      }
    } else {
      setSelectedClassId(null);
      form.resetFields();
    }
  }, [journalData, form, classes]);

  const handleClassChange = (classId: string) => {
    setSelectedClassId(classId);
    form.setFieldsValue({
      classId,
      subjectId: null,
      teacherId: null,
      timeRange: null,
    });
  };

  const handleSubjectChange = (subjectId: string) => {
    form.setFieldsValue({ subjectId });

    const cls = classes.find((c) => c.id === selectedClassId);
    const cs = cls?.classSubject?.find((item: any) => item.subjectId === subjectId);

    form.setFieldsValue({
      teacherId: cs?.teacherId || null,
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (!values.teacherId && !forTeacher) {
        message.error(t("teacherJournal.form.validation.teacher_required"));
        return;
      }

      const { timeRange, ...rest } = values;

      const payload: any = {
        ...rest,
        date: values.date ? values.date.format("YYYY-MM-DD") : null,
      };

      if (timeRange && timeRange[0] && timeRange[1]) {
        payload.periodLabel = `${timeRange[0].format("HH:mm")} - ${timeRange[1].format(
          "HH:mm"
        )}`;
      } else {
        payload.periodLabel = null;
      }

      await onSave(payload);
    } catch (error) {
      console.error("Error saving teacher journal:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal
      title={
        journalData
          ? t("teacherJournal.edit_title")
          : t("teacherJournal.add_title")
      }
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={journalData ? t("update") : t("save")}
    >
      <Form form={form} layout="vertical">
        {!forTeacher && (
          <>
            <Form.Item
              label={t("teacherJournal.form.class")}
              name="classId"
              rules={[
                {
                  required: true,
                  message: t("teacherJournal.form.validation.select_class"),
                },
              ]}
            >
              <CommonSelectLite
                disabled={!!journalData}
                placeholder={t("teacherJournal.form.select_class")}
                options={classOptions}
                value={form.getFieldValue("classId")}
                onChange={handleClassChange}
                optionValueKey="value"
                optionLabelKey="label"
              />
            </Form.Item>

            <Form.Item
              label={t("teacherJournal.form.subject")}
              name="subjectId"
              rules={[
                {
                  required: true,
                  message: t("teacherJournal.form.validation.select_subject"),
                },
              ]}
            >
              <CommonSelectLite
                placeholder={
                  selectedClass
                    ? t("teacherJournal.form.select_subject")
                    : t("teacherJournal.form.select_class_first")
                }
                options={subjectOptions ?? []}
                value={form.getFieldValue("subjectId")}
                onChange={handleSubjectChange}
                optionValueKey="value"
                optionLabelKey="label"
                disabled={!selectedClass || !!journalData}
              />
            </Form.Item>

            <Form.Item name="teacherId" hidden>
              <Input type="hidden" disabled={!!journalData} />
            </Form.Item>
          </>
        )}

        <Form.Item
          label={t("teacherJournal.form.date")}
          name="date"
          rules={[
            {
              required: true,
              message: t("teacherJournal.form.validation.select_date"),
            },
          ]}
        >
          <LocalizedDatePicker className="w-100" />
        </Form.Item>

        <Form.Item
          name="timeRange"
          label={t("time_range")}
          rules={[
            { required: false, message: t("time_range_required") },
            {
              validator: (_: any, value: any) => {
                if (value && value[0] && value[1]) {
                  const diff = dayjs(value[1], "HH:mm").diff(
                    dayjs(value[0], "HH:mm"),
                    "minute"
                  );
                  if (diff < 30) {
                    return Promise.reject(
                      t("validation.range_must_be_at_least_30m")
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TimePicker.RangePicker
            format="HH:mm"
            showNow={false}
            hideDisabledOptions
            hourStep={1}
            placeholder={[t("start"), t("end")]}
            disabledTime={(date, type) => ({
              disabledHours: () => {
                const allowed = [8, 9, 10, 11, 12, 14, 15, 16, 17, 18];
                return Array.from({ length: 24 }, (_, hour) => hour).filter(
                  (hour) => !allowed.includes(hour)
                );
              },
              disabledMinutes: (selectedHour) => {
                const allowed = [0, 15, 30, 45];
                return Array.from({ length: 60 }, (_, minute) =>
                  allowed.includes(minute) ? null : minute
                ).filter((m) => m !== null) as number[];
              },
            })}
          />
        </Form.Item>

        <Form.Item
          label={t("teacherJournal.form.session_number")}
          name="sessionNumber"
        >
          <InputNumber className="w-100" min={1} />
        </Form.Item>

        <Form.Item label={t("teacherJournal.form.competency")} name="competency">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item label={t("teacherJournal.form.objectives")} name="objectives">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          label={t("teacherJournal.form.prerequisites")}
          name="prerequisites"
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item label={t("teacherJournal.form.materials")} name="materials">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          label={t("teacherJournal.form.methodology")}
          name="methodology"
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item label={t("teacherJournal.form.evaluation")} name="evaluation">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item label={t("teacherJournal.form.homework")} name="homework">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item label={t("teacherJournal.form.notes")} name="notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddEditTeacherJournalModal;
